import { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { RequestCarrierState } from '@magimp/shared';
import { Button, EditorModal, Textarea } from '../../../../ui/index.js';
import { errorMessage } from '../../../../api/errors.js';
import { usePatchCarrier } from '../../queries.js';
import type { CarrierBid } from '../../types.js';
import { CarriersModalCancel, CarriersModalFooter } from './CarriersBidFormModal.js';

export interface CarriersRejectModalProps {
  requestId: string;
  bid: CarrierBid;
  onClose: () => void;
}

export function CarriersRejectModal({ requestId, bid, onClose }: CarriersRejectModalProps) {
  const { t } = useTranslation(['requests', 'common']);
  const patch = usePatchCarrier(requestId);
  const [reason, setReason] = useState(bid.rejectReason ?? '');
  const [submitError, setSubmitError] = useState<string | null>(null);
  // INVARIANT (#479): pin the initial value for the dirty-diff at first
  // render only — a background refetch must not overwrite typed input
  // nor the dirty baseline.
  const initialReasonRef = useRef(reason);

  async function onSubmit(): Promise<void> {
    setSubmitError(null);
    try {
      const rejectReason = reason.trim();
      await patch.mutateAsync({
        requestCarrierId: bid.id,
        body: {
          state: RequestCarrierState.REJECTED,
          ...(rejectReason.length > 0 ? { rejectReason } : {}),
        },
      });
      initialReasonRef.current = rejectReason;
      onClose();
    } catch (err) {
      setSubmitError(errorMessage(err));
    }
  }

  const dirty = reason.trim() !== initialReasonRef.current.trim();

  return (
    <EditorModal
      open
      dirty={dirty}
      busy={patch.isPending}
      onConfirmClose={onClose}
      title={t('requests:carriers.rejectTitle', { name: bid.carrier.name })}
      width={460}
    >
      <Textarea
        value={reason}
        onChange={setReason}
        placeholder={t('requests:carriers.rejectPlaceholder')}
        rows={4}
      />
      {submitError && (
        <div style={{ marginTop: 10, color: 'var(--status-error-fg)', fontSize: 12 }}>
          {submitError}
        </div>
      )}
      <CarriersModalFooter>
        <CarriersModalCancel onClose={onClose} label={t('common:actions.cancel')} />
        <Button
          kind="primary"
          disabled={patch.isPending}
          onClick={() => {
            void onSubmit();
          }}
        >
          {t('common:actions.save')}
        </Button>
      </CarriersModalFooter>
    </EditorModal>
  );
}
